"use client";

import { useMemo, useState, type FormEvent } from "react";
import { CheckIcon } from "@/components/icons/icons";
import { CONTACT_RECIPIENT } from "@/lib/site";
import type { Dictionary } from "@/i18n/dictionaries";

type Status = "idle" | "submitted";

// `datetime-local` requires `YYYY-MM-DDTHH:mm` with no timezone or seconds.
function toLocalDatetimeValue(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Default preferred meeting time:
 *   - Mon–Thu  → tomorrow
 *   - Fri/Sat/Sun → next Monday
 * at 13:00 Europe/Berlin (CET in winter, CEST in summer). The returned Date
 * is a real UTC instant; we then format it in the user's local timezone for
 * the `datetime-local` input value, so a user in Tokyo would see e.g.
 * "2026-05-19T21:00" (= 13:00 Berlin).
 */
function nextSuggestedMeeting(now: Date = new Date()): Date {
  // Day-of-week comes from the user's local calendar — that matches how a
  // user mentally reasons about "tomorrow".
  const day = now.getDay(); // 0=Sun … 6=Sat
  let daysToAdd: number;
  if (day === 5) daysToAdd = 3;        // Fri → Mon
  else if (day === 6) daysToAdd = 2;   // Sat → Mon
  else if (day === 0) daysToAdd = 1;   // Sun → Mon
  else daysToAdd = 1;                  // Mon–Thu → tomorrow

  const target = new Date(now);
  target.setDate(target.getDate() + daysToAdd);

  // Step 1: ask Europe/Berlin what calendar date `target` falls on.
  const berlinYMD = new Intl.DateTimeFormat("sv-SE", {
    timeZone: "Europe/Berlin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(target); // "YYYY-MM-DD"
  const [y, m, d] = berlinYMD.split("-").map(Number);

  // Step 2: probe Berlin's UTC offset on that calendar day by constructing a
  // tentative UTC instant at 13:00 and reading the hour Berlin reports back.
  // Then correct so the Berlin hour is exactly 13.
  const probe = new Date(Date.UTC(y, m - 1, d, 13, 0, 0));
  const berlinHour = parseInt(
    new Intl.DateTimeFormat("en-US", {
      timeZone: "Europe/Berlin",
      hour: "2-digit",
      hour12: false,
    }).format(probe),
    10,
  );
  return new Date(probe.getTime() - (berlinHour - 13) * 60 * 60 * 1000);
}

// Locale-aware "Mon, May 11 · 13:00 (your time)" style summary, useful as a
// human-readable confirmation of what the input value represents.
function formatLocalReadable(value: string, localeTag: string): string {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat(localeTag, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(d);
}

export function ContactForm({ dict, locale }: { dict: Dictionary; locale?: string }) {
  const [status, setStatus] = useState<Status>("idle");
  const [fullname, setFullname] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");

  // Compute the suggested default ONCE per mount. Memoizing also gives us
  // a stable `minDatetime` (the moment of first render) so the picker never
  // grays out future slots due to clock drift mid-session.
  const { defaultValue, minDatetime, readableLocale } = useMemo(() => {
    const suggested = nextSuggestedMeeting();
    return {
      defaultValue: toLocalDatetimeValue(suggested),
      minDatetime: toLocalDatetimeValue(new Date()),
      readableLocale: locale ?? (typeof navigator !== "undefined" ? navigator.language : "en"),
    };
  }, [locale]);

  const [preferredTime, setPreferredTime] = useState(defaultValue);

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const subject = encodeURIComponent(`Contact form: ${fullname}`);
    const preferredLine = preferredTime
      ? `Preferred time (user local): ${preferredTime.replace("T", " ")}\n\n`
      : "";
    const body = encodeURIComponent(
      `From: ${fullname} <${email}>\n\n${preferredLine}${message}`,
    );
    window.location.href = `mailto:${CONTACT_RECIPIENT}?subject=${subject}&body=${body}`;
    setStatus("submitted");
  }

  if (status === "submitted") {
    return (
      <div className="flex w-full max-w-[550px] flex-col items-center gap-4 rounded-2xl bg-white p-8 text-center shadow-[var(--shadow-md)]">
        <div className="grid size-12 place-items-center rounded-full bg-brand-soft text-brand">
          <CheckIcon className="size-6" />
        </div>
        <h2 className="text-[22px] font-medium tracking-[-0.022em] text-neutral-950">
          {dict.contact.successHeading}
        </h2>
        <p className="text-base leading-6 text-neutral-500">
          {dict.contact.successDescription}
        </p>
      </div>
    );
  }

  return (
    <form
      onSubmit={handleSubmit}
      className="flex w-full max-w-[550px] flex-col gap-8 rounded-2xl bg-white p-8 shadow-[var(--shadow-md)]"
    >
      <div className="flex flex-col gap-6">
        <Field id="contact-fullname" label={dict.contact.fields.fullname.label} required>
          <input
            id="contact-fullname"
            type="text"
            required
            placeholder={dict.contact.fields.fullname.placeholder}
            value={fullname}
            onChange={(e) => setFullname(e.target.value)}
            className="h-11 w-full rounded-xl border border-neutral-200 bg-white px-4 text-sm tracking-tight text-neutral-950 shadow-[var(--shadow-xs)] placeholder:text-neutral-400 focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand-ring"
          />
        </Field>
        <Field id="contact-email" label={dict.contact.fields.email.label} required>
          <input
            id="contact-email"
            type="email"
            required
            placeholder={dict.contact.fields.email.placeholder}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="h-11 w-full rounded-xl border border-neutral-200 bg-white px-4 text-sm tracking-tight text-neutral-950 shadow-[var(--shadow-xs)] placeholder:text-neutral-400 focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand-ring"
          />
        </Field>
        <Field
          id="contact-preferred-time"
          label={dict.contact.fields.preferredTime.label}
        >
          <div className="group relative">
            <CalendarGlyph className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 size-4 text-neutral-400 transition-colors group-focus-within:text-brand" />
            <input
              id="contact-preferred-time"
              type="datetime-local"
              value={preferredTime}
              min={minDatetime}
              onChange={(e) => setPreferredTime(e.target.value)}
              // - pl-10 reserves space for the leading calendar icon
              // - The arbitrary `[&::-webkit-calendar-picker-indicator]` rules
              //   stretch the native picker hit-area across the whole input
              //   and hide WebKit's default tiny calendar glyph; clicking
              //   anywhere on the field opens the picker.
              className="h-12 w-full cursor-pointer rounded-xl border border-neutral-200 bg-white pl-10 pr-4 text-sm font-medium tracking-tight text-neutral-950 shadow-[var(--shadow-xs)] transition-all hover:border-neutral-300 focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand-ring [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:inset-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-full [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0"
            />
            <CaretGlyph className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 size-3.5 text-neutral-400 transition-colors group-focus-within:text-brand" />
          </div>
          {preferredTime ? (
            <p className="mt-1.5 inline-flex items-center gap-1.5 text-xs tracking-tight text-neutral-500">
              <span className="inline-block size-1.5 rounded-full bg-brand" aria-hidden />
              <span>{formatLocalReadable(preferredTime, readableLocale)}</span>
              {preferredTime === defaultValue ? (
                <span className="ml-1 rounded-full bg-brand-soft px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-brand">
                  13:00 CET
                </span>
              ) : null}
            </p>
          ) : null}
        </Field>
        <Field id="contact-message" label={dict.contact.fields.message.label} required>
          <textarea
            id="contact-message"
            required
            placeholder={dict.contact.fields.message.placeholder}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={5}
            className="min-h-[140px] w-full resize-y rounded-xl border border-neutral-200 bg-white p-4 text-sm tracking-tight text-neutral-950 shadow-[var(--shadow-xs)] placeholder:text-neutral-400 focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand-ring"
          />
        </Field>
      </div>
      <button
        type="submit"
        className="inline-flex h-11 w-full items-center justify-center rounded-full bg-brand px-4 text-base font-medium tracking-tight text-white shadow-[0_1px_2px_0_rgba(14,18,27,0.08),inset_0_-1px_0_0_rgba(0,0,0,0.08)] transition-colors hover:bg-brand-strong focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand-ring"
      >
        {dict.contact.submit}
      </button>
    </form>
  );
}

type FieldProps = {
  id: string;
  label: string;
  required?: boolean;
  helper?: string;
  children: React.ReactNode;
};

function Field({ id, label, required, helper, children }: FieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor={id}
        className="flex items-center gap-1 text-sm font-medium tracking-tight text-neutral-950"
      >
        {label}
        {required ? <span className="text-rose-500">*</span> : null}
      </label>
      {children}
      {helper ? (
        <p className="mt-1 text-xs tracking-tight text-neutral-500">{helper}</p>
      ) : null}
    </div>
  );
}

function CalendarGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <rect x="2.25" y="3.25" width="11.5" height="10.5" rx="2" />
      <path d="M2.25 6.5h11.5" />
      <path d="M5.5 2v2.5M10.5 2v2.5" />
      <circle cx="5.6" cy="9.5" r="0.65" fill="currentColor" stroke="none" />
      <circle cx="8" cy="9.5" r="0.65" fill="currentColor" stroke="none" />
      <circle cx="10.4" cy="9.5" r="0.65" fill="currentColor" stroke="none" />
    </svg>
  );
}

function CaretGlyph({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <path d="m4 6 4 4 4-4" />
    </svg>
  );
}
